import Swal from "sweetalert2";
import { hasPermission } from "../core/common/adminPermissions";

export const creditTypeLabel = (type = "") =>
  ({
    limit_set: "Limit Updated",
    credit_used: "Credit Used (Udhaar)",
    payment_received: "Payment Received",
    credit_reversed: "Credit Reversed",
  })[type] || type.replaceAll("_", " ");

export const creditStatusLabel = (status = "") =>
  ({
    outstanding: "Due (Unpaid)",
    partially_settled: "Partially Paid",
    settled: "Fully Paid",
    applied: "Received & Applied",
    reversed: "Reversed",
  })[status] || status.replaceAll("_", " ");

export const toneForCreditType = (type = "") => {
  if (type === "payment_received") return "success";
  if (type === "credit_reversed") return "warning";
  if (type === "credit_used") return "danger";
  return "muted";
};

export const toneForCreditStatus = (status = "") => {
  if (status === "settled" || status === "applied") return "success";
  if (status === "partially_settled" || status === "outstanding")
    return "warning";
  if (status === "reversed") return "danger";
  return "muted";
};

export const canManageCredit = (): boolean => hasPermission("credit_manage");

export const toneForOrderCreditStatus = (status = "") => {
  if (status === "Settled") return "success";
  if (status === "Partially Settled") return "warning";
  if (status === "Reversed") return "danger";
  return "warning";
};

export const paymentMethodLabel = (method?: string) => {
  if (method === "cod") return "Cash on Delivery";
  if (method === "online") return "Online Payment";
  if (method === "credit") return "Store Credit";
  return "-";
};

export const roleTone = (role = "") => {
  if (role === "superadmin") return "warning";
  if (role === "manager") return "success";
  return "muted";
};

export const formatAdminRole = (role = "") =>
  role ? role.charAt(0).toUpperCase() + role.slice(1) : "—";

export type TrackingForm = {
  courierName: string;
  trackingNumber: string;
  trackingUrl: string;
  estimatedDelivery: string;
  note: string;
};

export const toDateInputValue = (value?: string | Date | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const trackingFromOrder = (order?: any): TrackingForm => ({
  courierName: order?.trackingInfo?.courierName || "",
  trackingNumber: order?.trackingInfo?.trackingNumber || "",
  trackingUrl: order?.trackingInfo?.trackingUrl || "",
  estimatedDelivery: toDateInputValue(order?.trackingInfo?.estimatedDelivery),
  note: order?.trackingInfo?.note || "",
});

export const promptTrackingDetails = async (
  existing?: Partial<TrackingForm> | null,
  options?: { required?: boolean },
) => {
  const current = {
    courierName: existing?.courierName || "",
    trackingNumber: existing?.trackingNumber || "",
    trackingUrl: existing?.trackingUrl || "",
    estimatedDelivery: existing?.estimatedDelivery || "",
    note: existing?.note || "",
  };

  const result = await Swal.fire({
    title: "Shipment Tracking",
    html: `
      <input id="trk-courier" class="swal2-input" placeholder="Courier name (Delhivery, Bluedart...)" value="${current.courierName.replace(/"/g, "&quot;")}" />
      <input id="trk-number" class="swal2-input" placeholder="Tracking / AWB number" value="${current.trackingNumber.replace(/"/g, "&quot;")}" />
      <input id="trk-url" class="swal2-input" placeholder="Tracking URL (optional)" value="${current.trackingUrl.replace(/"/g, "&quot;")}" />
      <input id="trk-eta" class="swal2-input" type="date" value="${current.estimatedDelivery}" />
      <input id="trk-note" class="swal2-input" placeholder="Note (optional)" value="${current.note.replace(/"/g, "&quot;")}" />
    `,
    showCancelButton: true,
    showDenyButton: !options?.required,
    confirmButtonText: "Save Tracking",
    denyButtonText: "Skip",
    confirmButtonColor: "#9b6c2b",
    preConfirm: () => {
      const courierName = (
        document.getElementById("trk-courier") as HTMLInputElement
      )?.value.trim();
      const trackingNumber = (
        document.getElementById("trk-number") as HTMLInputElement
      )?.value.trim();
      const trackingUrl = (
        document.getElementById("trk-url") as HTMLInputElement
      )?.value.trim();
      const estimatedDelivery = (
        document.getElementById("trk-eta") as HTMLInputElement
      )?.value;
      const note = (
        document.getElementById("trk-note") as HTMLInputElement
      )?.value.trim();

      if (!courierName || !trackingNumber) {
        Swal.showValidationMessage("Courier name and tracking number are required");
        return undefined;
      }

      if (trackingUrl && !/^https?:\/\//i.test(trackingUrl)) {
        Swal.showValidationMessage("Tracking URL must start with http:// or https://");
        return undefined;
      }

      return {
        courierName,
        trackingNumber,
        trackingUrl,
        estimatedDelivery,
        note,
      } as TrackingForm;
    },
  });

  if (result.isConfirmed) return result.value as TrackingForm;
  if (result.isDenied) return "skipped" as const;
  return null;
};
